import React, { useEffect } from 'react';
import { useNavigate } from 'react-router-dom';

import { Brightness1 } from '@mui/icons-material';
import { Grid } from '@mui/material';

import { TableComponent } from 'commons/components/Table';
import S from 'commons/styles/sto-list-page.styles';
import { ProductDetail, STOItem } from 'commons/types';
import { STO_PICK_ROUTES } from 'routes/constants';

import { config } from '../STOPickListTable.config';

interface ISTOPickListTableProps {
  stoItems: STOItem[];
  setDisableProceedButton: (value: boolean) => void;
}

export const STOPickListTable: React.FC<ISTOPickListTableProps> = ({
  stoItems,
  setDisableProceedButton,
}) => {
  const navigate = useNavigate();

  useEffect(() => {
    const disableProceedButton = stoItems.reduce<boolean>((result, item) => {
      if (!result) {
        return !!item.pick_actual.find((actual) => !actual.basket);
      }
      return result;
    }, false);
    setDisableProceedButton(disableProceedButton);
  }, [stoItems, setDisableProceedButton]);

  return stoItems.map((item, idx) => {
    const pickedProgress = item.pick_actual.reduce(
      (sum, pickActual) => sum + pickActual.quantity,
      0,
    );
    const errorMessage = item.pick_actual.find((actual) => !actual.basket)
      ? 'Need to fill basket value!'
      : '';

    const colorProgress =
      pickedProgress === 0
        ? S.colors.PROGRESS_ICON_COLOR[0]
        : pickedProgress !== item.quantity
        ? S.colors.PROGRESS_ICON_COLOR[2]
        : S.colors.PROGRESS_ICON_COLOR[1];
    const skuButtonText = pickedProgress === 0 ? 'PICK SKU' : 'EDIT SKU';

    const getTableData = (): Partial<ProductDetail>[] => {
      return item.bin_recommendation && item.bin_recommendation.length > 0
        ? item.bin_recommendation
        : [
            {
              bin: '-',
              bin_type: '-',
            } as Partial<ProductDetail>,
          ];
    };

    return (
      <Grid container>
        <S.SKUGridWrapper container>
          <S.SKUGrid
            className="bold"
            item
            xs={8}
            data-testid={`TextListSKU-${idx + 1}`}
          >
            {item.sku_code}
            <S.ProgressSpan data-testid={`TextListQTYPicked-${idx + 1}`}>
              <Brightness1 style={{ fontSize: 11, color: colorProgress }} />{' '}
              {pickedProgress}/{item.quantity}
            </S.ProgressSpan>
          </S.SKUGrid>
          <S.SKUButtonGrid item xs={4}>
            <S.SecondaryButton
              onClick={(): void =>
                navigate(STO_PICK_ROUTES.ITEM, {
                  state: { pickIdx: idx },
                })
              }
              data-testid={`BtnListPick-${idx + 1}`}
            >
              {skuButtonText}
            </S.SecondaryButton>
          </S.SKUButtonGrid>
          <S.SKUGrid xs={12}>
            STO Number: <S.STONumberText>{item.sto_number}</S.STONumberText>
          </S.SKUGrid>
        </S.SKUGridWrapper>
        <S.TableInfoGridWrapper container>
          <S.TableInfoGrid item xs={12}>
            <TableComponent config={config} data={getTableData() || []} />
          </S.TableInfoGrid>
          <S.ErrorMessage item xs={12} data-testid="STOPickTestError">
            {errorMessage}
          </S.ErrorMessage>
        </S.TableInfoGridWrapper>
      </Grid>
    );
  });
};
